import type { OfferRedeemMode } from './new-client-offer-enums';

/**
 * How a guest uses the New Client Offer at this salon, from the salon's check-in switch (`howToRedeem`) and its stand:
 *  - `STAND`: no check-in, a Hayven Tap stand at the front desk — tap it, press "Use at checkout", show the screen;
 *  - `SIGN`: no check-in, no stand — scan the QR code on the counter sign (named by place, never by its printed
 *    headline) and type the phone number;
 *  - `CHECK_IN`: book, then check in with the phone number; the visit marked Done uses it.
 * One fact per salon: pages and texts both read it, so a stand fitted later changes every future page and text.
 */
export type OfferRedeemWayKind = 'STAND' | 'SIGN' | 'CHECK_IN';

export function toOfferRedeemWayKind(howToRedeem: OfferRedeemMode, counterStand: boolean): OfferRedeemWayKind {
  if (howToRedeem !== 'COUNTER_QR') return 'CHECK_IN';
  return counterStand ? 'STAND' : 'SIGN';
}

/**
 * What the guest does at the salon, in the same words on every page and text ("the stand at the front desk", "the QR
 * code at the counter"). Lower-case, so a sentence can lead with it or carry it mid-way (`capitalizeOfferWayPhrase`).
 */
export const OFFER_WAY_ACTION: Record<Exclude<OfferRedeemWayKind, 'CHECK_IN'>, string> = {
  STAND: 'tap your phone on the stand at the front desk',
  SIGN: 'scan the QR code at the counter',
};

/** `{{offer.useAction}}`: the whole action, for a text with room for it. A check-in salon keeps its own words. */
export const OFFER_TEXT_USE_ACTION: Record<OfferRedeemWayKind, string> = {
  ...OFFER_WAY_ACTION,
  CHECK_IN: 'just use this phone number',
};

/** `{{offer.useShort}}`: the same action in a sentence that must stay in one segment. */
export const OFFER_TEXT_USE_SHORT: Record<OfferRedeemWayKind, string> = {
  STAND: 'tap the stand',
  SIGN: 'scan the QR',
  CHECK_IN: OFFER_TEXT_USE_ACTION.CHECK_IN,
};

/** `{{offer.useOnArrival}}`: a closing sentence, empty at a check-in salon, whose guests have nothing to do on arrival. */
export const OFFER_TEXT_USE_ON_ARRIVAL: Record<OfferRedeemWayKind, string> = {
  STAND: 'Tap the stand when you arrive.',
  SIGN: 'Scan the QR when you arrive.',
  CHECK_IN: '',
};

/** Where the guest finds what they tap or scan, under its picture. */
export const OFFER_WAY_FIND_LINE: Record<Exclude<OfferRedeemWayKind, 'CHECK_IN'>, string> = {
  STAND: "You'll find it at the front desk.",
  SIGN: "You'll find it at the counter.",
};

/** "tap your phone…" → "Tap your phone…". */
export function capitalizeOfferWayPhrase(phrase: string): string {
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

export const ARRIVAL_LINE_TOKEN = /\s*\{\{\s*offer\.useOnArrival\s*\}\}/g;

/** A text of nothing but the arrival line would be empty at a check-in salon, so saves refuse it. */
export function isOnlyOfferArrivalLine(body: string): boolean {
  return body.replace(ARRIVAL_LINE_TOKEN, '').trim() === '' && body.trim() !== '';
}
